import { Component, HostListener, Input, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { ConfirmationService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { UserInfo, UserService } from "../../../../shared";
import { LoginService } from "../../services/login.service";
import * as moment from "moment";
import * as shajs from "sha.js";
import { EndpointLoginStep1Request } from "../../services/EndpointLoginStep1Request";
import { EndpointLoginStep2Request } from "../../services/EndpointLoginStep2Request";
import { StringRequest } from "../../services/StringRequest";

@Component({
	selector: "caass-su-login",
	templateUrl: "./su-login.component.html",
	styleUrls: ["./su-login.component.css"],
})
export class SuLoginComponent implements OnInit {
	constructor(
		private loginService: LoginService,
		private userService: UserService,
		private router: Router,
		private confirmationService: ConfirmationService,
		private dialogService: DialogService,
		private title: Title
	) {}

	ngOnInit(): void {
		this.loadLoginPageInfo();
	}

	//////////////////////////////////////////////////////////////
	// Data
	//////////////////////////////////////////////////////////////

	//
	//	Logo/Misc. Information
	//

	DistrictInfoLoading: boolean = true;
	ShowDistrictName: boolean = false;
	ServerName: string = "";
	DistrictDisplayName: string = "";
	DistrictVersionNumber: string = "";
	VersionChecked: boolean = false;
	VersionMatch: boolean = false;

	@Input() CAASS_VERSION: string = "";

	//
	//	Login Info
	//

	LoginStep: number = 1;

	Username: string = "";
	Password: string = "";

	SU_UserID: number = -1;
	CAASS_UserID: number = 0;
	TempToken: string = "";
	ShowQRCode: boolean = false;
	QRCodeB64: string = "";
	QRManualCode: string = "";
	MFACode: string = "";

	errorMessage: string = "";
	Processing: boolean = false;

	@HostListener("window:keydown", ["$event"])
	LogKeys(e: KeyboardEvent) {
		if (this.IsLoading() == true) {
			return;
		}

		//
		// Pressing Enter tries to go to the next page
		//
		if (e.keyCode === 13) {
			try {
				if (this.LoginStep == 1) {
					var button = <HTMLButtonElement>document.getElementById("LoginButton");
					button.click();
				} else {
					var button = <HTMLButtonElement>document.getElementById("LoginMFAButton");
					button.click();
				}
			} catch (e) {
				e.toString();
			}
		}
	}

	//////////////////////////////////////////////////////////////

	//////////////////////////////////////////////////////////////
	// Load Data
	//////////////////////////////////////////////////////////////

	loadLoginPageInfo() {
		this.DistrictInfoLoading = true;

		var result = this.loginService.LoginPageInfoSelect();
		result.subscribe(
			(response: any) => {
				this.ServerName = response.ServerName;
				this.ShowDistrictName = response.ShowDistrictName;
				this.DistrictDisplayName = response.DistrictDisplayName;
				this.DistrictVersionNumber = response.DistrictVersionNumber;

				this.title.setTitle(this.ServerName + "-Login");

				this.CheckVersion();

				this.DistrictInfoLoading = false;
			},
			(error) => {}
		);
	}

	CheckVersion() {
		this.VersionMatch = String(this.CAASS_VERSION) == String(this.DistrictVersionNumber);

		if (this.VersionMatch == false) {
			console.log("Local Version");
			console.log(this.CAASS_VERSION);
			console.log("District Version Number");
			console.log(this.DistrictVersionNumber);
		}

		this.VersionChecked = true;
	}

	//////////////////////////////////////////////////////////////

	//////////////////////////////////////////////////////////////
	// Buttons
	//////////////////////////////////////////////////////////////

	onLoginStep1() {
		this.errorMessage = "";

		if (this.Username == undefined || this.Username == "") {
			this.errorMessage = "Enter Username";
			return;
		}

		if (this.Password == undefined || this.Password == "") {
			this.errorMessage = "Enter Password";
			return;
		}

		var PasswordHash = String(shajs("sha256").update(this.Password).digest("hex")).toUpperCase();

		var parms: EndpointLoginStep1Request = {
			CAASS_Token: "", // Filled in by server
			Domain: "", // Filled in by server
			Username: this.Username,
			Password: PasswordHash,
		};

		this.Processing = true;

		var result = this.loginService.EndpointLoginStep1(parms);
		result.subscribe(
			(response: any) => {
				this.Processing = false;

				if (response.Success == true) {
					//
					//	Legacy, Skip MFA and login as SU using CAASS.Utils
					//
					if (response.SkipMFA == true) {
						this.FinishLogin(0, 0, 1, response.SkipAccessToken, response.SkipExpiration);
					}
					//
					//	Otherwise continue normally
					//
					else {
						//
						//	Move on to MFA Step
						//
						this.LoginStep = 2;
						this.SU_UserID = response.SU_UserID;
						this.CAASS_UserID = response.CAASS_UserID;
						this.TempToken = response.TempToken;
						this.ShowQRCode = response.ShowQRCode;
						this.QRCodeB64 = response.QRCodeB64;
						this.QRManualCode = response.QRManualCode;
					}
				} else {
					this.errorMessage = response.Msg;
				}
			},
			(error) => {
				this.Processing = false;
				console.log(error);
				this.errorMessage = error.Message;
			}
		);
	}

	onLoginStep2() {
		this.errorMessage = "";

		if (this.MFACode == undefined || this.MFACode == "") {
			this.errorMessage = "Enter MFA Code";
			return;
		}

		var UsernameSplit = this.Username.split("=");
		var UsernameNoAlias = UsernameSplit[0];
		var Alias = "";

		if (UsernameSplit.length >= 2) {
			Alias = UsernameSplit[1];
		}

		var parms: EndpointLoginStep2Request = {
			CAASS_Token: "", // Filled in by server
			Domain: "", // Filled in by server
			Alias: Alias,
			CAASS_UserID: this.CAASS_UserID,
			SU_UserID: this.SU_UserID,
			TempToken: this.TempToken,
			MFACode: this.MFACode,
		};

		this.Processing = true;

		var result = this.loginService.EndpointLoginStep2(parms);
		result.subscribe(
			(response: any) => {
				this.Processing = false;

				if (response.Success == true) {
					this.FinishLogin(response.UserID, response.PersonID, response.UserLevelID, response.AccessToken, response.Expiration);
				} else {
					this.errorMessage = response.Msg;
				}
			},
			(error) => {
				this.Processing = false;
				console.log(error);
				this.errorMessage = error.Message;
			}
		);
	}

	FinishLogin(UserID: number, PersonID: number, UserLevelID: number, AccessToken: string, Expiration: string) {
		var user = new UserInfo();

		user.c_date = moment(new Date()).format("MM/DD/YYYY");
		user.isLoggedIn = true;
		user.token = AccessToken;
		user.refresh_token = "";
		user.token_expiration = Expiration;
		user.userID = UserID;
		user.personID = PersonID;
		user.userName = this.Username;
		user.UserLevelID = UserLevelID;

		this.userService.setCurrentUser(user);
		this.router.navigate([""]);
	}

	onForgotPassword() {
		var parms: StringRequest = {
			str: this.Username,
		};

		this.Processing = true;

		var result = this.loginService.SuPasswordResetRequest(parms);
		result.subscribe(
			(response: any) => {
				this.Processing = false;

				if (response.Success == true) {
					this.confirmationService.confirm({
						key: "login3",
						acceptLabel: "Ok",
						rejectVisible: false,
						message: "We've emailed you a Password Reset Link to your email. You should be receiving it shortly.",
						header: "Reset Password",
						icon: "fa",
						accept: () => {
							this.Processing = false;
						},
					});
				} else {
					this.confirmationService.confirm({
						key: "login3",
						acceptLabel: "Ok",
						rejectVisible: false,
						message: "Error processing reset password request: " + response.Msg,
						header: "ERROR",
						icon: "fa",
						accept: () => {
							this.Processing = false;
						},
					});
				}
			},
			(error) => {
				this.Processing = false;
			}
		);
	}

	onForgotMFA() {
		var parms: StringRequest = {
			str: this.Username,
		};

		this.Processing = true;

		var result = this.loginService.SuMFAResetRequest(parms);
		result.subscribe(
			(response: any) => {
				this.Processing = false;

				if (response.Success == true) {
					this.confirmationService.confirm({
						key: "login3",
						acceptLabel: "Ok",
						rejectVisible: false,
						message: "Please check your email for a new MFA Code.",
						header: "Reset Password",
						icon: "fa",
						accept: () => {
							this.Processing = false;
						},
					});
				} else {
					this.confirmationService.confirm({
						key: "login3",
						acceptLabel: "Ok",
						rejectVisible: false,
						message: "Error sending reset MFA email: " + response.Msg,
						header: "ERROR",
						icon: "fa",
						accept: () => {
							this.Processing = false;
						},
					});
				}
			},
			(error) => {
				this.Processing = false;
			}
		);
	}

	//////////////////////////////////////////////////////////////

	//////////////////////////////////////////////////////////////
	// Helpers
	//////////////////////////////////////////////////////////////

	IsLoading() {
		return this.Processing == true;
	}

	//////////////////////////////////////////////////////////////
}
